/**
 * colorHash.js
 * Deterministic color + initial generators for clinic avatar fallbacks.
 *
 * Used by ClinicCard.jsx when a clinic has no logo uploaded — we hash the
 * clinic's name into one of 8 palette colors so the same clinic always gets
 * the same color across renders, pagination, and reloads.
 */

// ── Palette ──────────────────────────────────────────────────────────────────
// 8 colors, all chosen to give sufficient contrast against white text.
// Ordered: red, pink, purple, deep purple, blue, teal, green, orange.
const PALETTE = [
  '#E57373', // red 300
  '#F06292', // pink 300
  '#BA68C8', // purple 300
  '#9575CD', // deep purple 300
  '#64B5F6', // blue 300
  '#4DB6AC', // teal 300
  '#81C784', // green 300
  '#FFB74D', // orange 300
];

/**
 * Hash a clinic name into one of the palette colors.
 * Uses the classic djb2-style 32-bit string hash so the result is stable
 * across reloads and identical for identical input strings.
 *
 * @param {string} name  Clinic name (Thai or Latin both supported)
 * @returns {string}     Hex color from PALETTE
 */
export function colorFromName(name = '') {
  const str = String(name ?? '');
  if (!str.length) {
    return PALETTE[0];
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    // (hash * 33) + charCode  — using shift form for speed
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // force 32-bit int
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

/**
 * Get the first character of a clinic name for the avatar circle.
 * Handles Thai names (single characters render correctly via String.charAt /
 * Array spread), trims whitespace, and falls back to "?" when empty.
 *
 * @param {string} name  Clinic name
 * @returns {string}     One-character initial, uppercased for Latin scripts
 */
export function initialFromName(name = '') {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) {
    return '?';
  }

  // Use Array spread to grab the first full code point (correct for emoji /
  // surrogate pairs, though most clinic names are plain Thai or Latin).
  const first = [...trimmed][0] || '?';
  return first.toUpperCase();
}

export default { colorFromName, initialFromName };
